import i18next from "i18next";
import { initReactI18next } from "react-i18next";
import en_GB from "./English_en-GB.json";
import { store } from "../index";
import { setLanguages, setUser } from "@redux/actions/index";
import { getUserAttribute } from "layouts/common";
import utils from "utils";
import api from "services/backendService";

// The published JSON comes over the network: never let it reach an object's prototype.
const UNSAFE_KEYS = new Set(["__proto__", "constructor", "prototype"]);

const isPlainObject = (value) =>
  value !== null && typeof value === "object" && !Array.isArray(value);

// Returns a new object: `override` wins, `base` fills what it lacks. Neither is mutated.
export const mergeDeep = (base, override) => {
  const result = { ...base };
  for (const [key, value] of Object.entries(override ?? {})) {
    if (UNSAFE_KEYS.has(key)) continue;
    result[key] =
      isPlainObject(value) && isPlainObject(base?.[key])
        ? mergeDeep(base[key], value)
        : value;
  }
  return result;
};

export const initLanguage = async (language = null) => {
  const tempLang = localStorage.getItem("tempLanguage");
  if (tempLang) {
    language = tempLang;
  }
  const userAttribute = await getUserAttribute();
  if (userAttribute && !language) {
    language = userAttribute.language || "English_en-GB";
  }
  store.dispatch(setUser({ userAttribute }));
  try {
    const lang =
      language ?? (store.getState().user.language || "English_en-GB");
    const languages = await api.getLanguagesList();
    const translations = (await api.getTranslations(lang)).data;
    store.dispatch(setLanguages({ list: languages }));

    const code = lang.split("_")[1];

    utils.debug("Setting current language", code);

    await i18next.use(initReactI18next).init({
      interpolation: { escapeValue: false },
      lng: code,
      fallbackLng: "en-GB",
      fallbackNS: "common",
      ns: ["common"],
      defaultNS: "common",
      // For an en-GB user both entries share the same key: merge instead of
      // overwriting, so published strings win and keys not published yet still
      // resolve from the bundled copy rather than showing the raw key.
      resources: {
        "en-GB": {
          common: code === "en-GB" ? mergeDeep(en_GB, translations) : en_GB,
        },
        ...(code !== "en-GB" && { [code]: { common: translations } }),
      },
    });
  } catch (error) {
    utils.debug("Error loading translations. Local fallback activated.", error);
    // local and static fallback
    await i18next.use(initReactI18next).init({
      interpolation: { escapeValue: false },
      lng: "en-GB",
      fallbackLng: false,
      ns: ["common"],
      defaultNS: "common",
      resources: {
        "en-GB": { common: en_GB },
      },
    });
  }
};
